import { Badge, Card, Group, Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";

import { useGetPolls } from "../../../api/insights";
import { ResponseBreakdown } from "../charts";
import SectionShell, {
  usePartShown,
  EmptyState,
  Stat,
  StatGrid,
  formatNum,
  formatPct,
} from "../SectionShell";

const POLL_TYPE_LABEL = {
  singleChoice: "Single choice",
  multipleChoice: "Multiple choice",
  text: "Text",
};

const PollBreakdown = ({ breakdown }) => {
  if (!breakdown) return null;
  return (
    <ResponseBreakdown
      type={breakdown.kind}
      options={breakdown.options}
      answers={breakdown.answers}
    />
  );
};

const PollMeta = ({ poll }) => (
  <Group gap="xs" wrap="wrap">
    <Badge variant="light" color="blue" size="sm">
      {POLL_TYPE_LABEL[poll.type] ?? poll.type ?? "—"}
    </Badge>
    <Text size="xs" c="dimmed">
      {formatNum(poll.totalResponses)} responses ·{" "}
      {formatNum(poll.uniqueRespondents)} unique respondents
    </Text>
  </Group>
);

const PollCard = ({ poll, isFollowUp = false }) => (
  <Card
    withBorder
    padding="md"
    radius="md"
    bg={isFollowUp ? "white" : "gray.0"}
  >
    <Stack gap="sm">
      <Stack gap={4}>
        <Group gap="xs" wrap="wrap" align="center">
          {isFollowUp && (
            <Badge variant="outline" color="grape" size="xs">
              Follow-up
            </Badge>
          )}
          <Text fw={600}>{poll.title ?? "(untitled poll)"}</Text>
        </Group>
        <PollMeta poll={poll} />
      </Stack>
      <PollBreakdown breakdown={poll.breakdown} />
    </Stack>
  </Card>
);

function PollsAnalysis({ eventId }) {
  const { data, isPending, isError, error } = useGetPolls(eventId);
  const showBreakdown = usePartShown("perPollBreakdown");

  const polls = useMemo(() => data?.polls ?? [], [data]);

  return (
    <SectionShell
      id="pollsAnalysis"
      title="Polls"
      description="Live poll participation across the event."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="pollsAnalysis"
    >
      <StatGrid>
        <Stat
          part="totalPolls"
          label="Total polls"
          value={formatNum(data?.totalPolls)}
        />
        <Stat
          part="totalResponses"
          label="Total responses"
          value={formatNum(data?.totalResponses)}
        />
        <Stat
          part="participants"
          label="Participants"
          value={formatNum(data?.participants)}
        />
        <Stat
          part="participationRate"
          label="Participation rate"
          value={formatPct(data?.participationRate)}
        />
        <Stat
          part="avgResponsesPerPoll"
          label="Avg responses / poll"
          value={
            Number.isFinite(Number(data?.avgResponsesPerPoll))
              ? Number(data.avgResponsesPerPoll).toFixed(2)
              : "—"
          }
        />
      </StatGrid>

      {showBreakdown &&
        (polls.length === 0 ? (
          <EmptyState />
        ) : (
          <Stack gap="md">
            <Title order={5}>Per-poll breakdown</Title>
            {polls.map((p) => (
              <Stack key={p.pollId} gap="xs">
                <PollCard poll={p} />
                {p.followUps?.length > 0 && (
                  <Stack gap="xs" pl="lg">
                    {p.followUps.map((fu) => (
                      <PollCard key={fu.pollId} poll={fu} isFollowUp />
                    ))}
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        ))}
    </SectionShell>
  );
}

export default PollsAnalysis;
